export const FINDINGS_TEMPLATE = `Papila / nervio óptico: bordes, color, excavación (C/D), hemorragias en astilla, palidez, edema, etc.
Vasos: calibre, tortuosidad, cruces A-V, hemorragias, microaneurismas, neovasos, etc.
Mácula: reflejo foveal, exudados, edema, drusas, cambios pigmentarios, membrana epirretinal, etc.
Retina periférica / cuadrantes (si la imagen lo permite): lesiones y su localización (p. ej. "superotemporal", "inferonasal"). La descripción clásica se apoya en cuadrantes y referencias anatómicas como disco/mácula.`

export const RECAPTURE_ACTION = 'Repetir estudio (imágenes de mala calidad)'

export const IMMEDIATE_ACTION_OPTIONS = [
  RECAPTURE_ACTION,
  'Repetir control en 6 meses',
  'Repetir control en 1 año',
  'Revisión oftalmológica recomendada',
  'Revisión oftalmológica urgente para tratamiento',
  'Otro'
]

export const isRecaptureAction = (action) => {
  return action === RECAPTURE_ACTION
}

const SECTION_HEADERS = {
  findingsRight: 'HALLAZGOS OJO DERECHO:',
  findingsLeft: 'HALLAZGOS OJO IZQUIERDO:',
  diagnosis: 'IMPRESION DIAGNOSTICA:',
  recommendations: 'CONDUCTA / RECOMENDACIONES:',
  pathologies: 'PATOLOGIAS CONCOMITANTES:',
  imageQuality: 'CALIDAD DE IMAGEN (REVISION PROFESIONAL):'
}

const QUALITY_LABELS = {
  buena: 'Buena',
  aceptable: 'Aceptable',
  mala: 'Mala'
}

const QUALITY_VALUE_TO_CODE = {
  mala: 0,
  buena: 1,
  aceptable: 2
}

const QUALITY_CODE_TO_VALUE = {
  0: 'mala',
  1: 'buena',
  2: 'aceptable'
}

const normalizeBoolean = (value, fallback = false) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  if (value === false || value === 'false' || value === 0 || value === '0') return false
  return fallback
}

const parseBooleanSelection = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  if (value === false || value === 'false' || value === 0 || value === '0') return false
  return ''
}

const normalizeInteger = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? fallback : parsed
}

const parseIntegerSelection = (value) => {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? '' : parsed
}

const asText = (value) => {
  if (typeof value === 'string') return value
  return ''
}

const normalizeEye = (value) => (value === 'left' ? 'left' : 'right')

const normalizeQualityCode = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(QUALITY_VALUE_TO_CODE, value)) {
    return QUALITY_VALUE_TO_CODE[value]
  }

  if (typeof value === 'number' && Number.isInteger(value) && Object.prototype.hasOwnProperty.call(QUALITY_CODE_TO_VALUE, value)) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!/^\d+$/.test(normalized)) return null
    const parsed = Number.parseInt(normalized, 10)
    return Object.prototype.hasOwnProperty.call(QUALITY_CODE_TO_VALUE, parsed) ? parsed : null
  }

  return null
}

const normalizeQualityValue = (value) => {
  if (value === null || value === undefined || value === '') return ''

  if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(QUALITY_VALUE_TO_CODE, value)) {
    return value
  }

  const parsed = Number.parseInt(value, 10)
  if (Number.isNaN(parsed)) return ''
  return QUALITY_CODE_TO_VALUE[parsed] || ''
}

const extractSection = (fullText, startHeader, endHeader) => {
  const startIndex = fullText.indexOf(startHeader)
  if (startIndex < 0) return ''

  const contentStart = startIndex + startHeader.length
  const endIndex = endHeader ? fullText.indexOf(endHeader, contentStart) : fullText.length
  const contentEnd = endIndex < 0 ? fullText.length : endIndex
  return fullText.slice(contentStart, contentEnd).trim()
}

const normalizeAction = (action) => {
  if (IMMEDIATE_ACTION_OPTIONS.includes(action)) return action
  return ''
}

const parsePathologyBlock = (block) => {
  const sanitizedBlock = asText(block).trim()
  if (!sanitizedBlock) {
    return {
      hasPathology: '',
      detail: ''
    }
  }

  if (/^sin definir$/i.test(sanitizedBlock)) {
    return {
      hasPathology: '',
      detail: ''
    }
  }

  if (/^no$/i.test(sanitizedBlock)) {
    return {
      hasPathology: false,
      detail: ''
    }
  }

  const explicitYes = sanitizedBlock.match(/^si:\s*(.*)$/i)
  if (explicitYes) {
    return {
      hasPathology: true,
      detail: explicitYes[1] || ''
    }
  }

  return {
    hasPathology: true,
    detail: sanitizedBlock
  }
}

const parseRecommendationsBlock = (block) => {
  const actionRegex = /Accion inmediata recomendada:\s*(.*)/i
  const actionMatch = block.match(actionRegex)
  const actionValue = actionMatch ? actionMatch[1].trim() : ''
  const detailMarker = 'Detalle:'
  const detailIndex = block.indexOf(detailMarker)
  const detail = detailIndex >= 0
    ? block.slice(detailIndex + detailMarker.length).trim()
    : block.replace(actionRegex, '').trim()

  return {
    action: normalizeAction(actionValue),
    detail
  }
}

const parseImageQualityBlock = (block) => {
  const entries = {}
  const lines = asText(block)
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  lines.forEach((line) => {
    const match = line.match(/^-\s*Ojo\s*(derecho|izquierdo),\s*imagen\s*(\d+|-)\s*\(id:\s*([^)]+)\):\s*(Buena|Aceptable|Mala)$/i)
    if (!match) return

    const [, eyeLabel, imagePositionStr, imageId, qualityLabel] = match
    const position = imagePositionStr === '-' ? 0 : Number.parseInt(imagePositionStr, 10)
    entries[imageId] = {
      value: qualityLabel.toLowerCase(),
      eye: eyeLabel.toLowerCase() === 'izquierdo' ? 'left' : 'right',
      position
    }
  })

  return entries
}

export const parseStructuredReportText = (textoLibre = '') => {
  const safeText = asText(textoLibre).trim()
  if (!safeText) {
    return {
      hallazgos_ojo_derecho: '',
      hallazgos_ojo_izquierdo: '',
      impresion_diagnostica: '',
      accion_inmediata: '',
      conducta_recomendaciones: '',
      patologia_concomitante_detalle: '',
      calidad_imagenes: {},
      isStructured: false
    }
  }

  const isStructured = safeText.includes(SECTION_HEADERS.findingsRight) &&
    safeText.includes(SECTION_HEADERS.findingsLeft) &&
    safeText.includes(SECTION_HEADERS.diagnosis) &&
    safeText.includes(SECTION_HEADERS.recommendations)

  if (!isStructured) {
    return {
      hallazgos_ojo_derecho: '',
      hallazgos_ojo_izquierdo: '',
      impresion_diagnostica: '',
      accion_inmediata: '',
      conducta_recomendaciones: safeText,
      patologia_concomitante_detalle: '',
      calidad_imagenes: {},
      isStructured: false
    }
  }

  const findingsRight = extractSection(safeText, SECTION_HEADERS.findingsRight, SECTION_HEADERS.findingsLeft)
  const findingsLeft = extractSection(safeText, SECTION_HEADERS.findingsLeft, SECTION_HEADERS.diagnosis)
  const diagnosis = extractSection(safeText, SECTION_HEADERS.diagnosis, SECTION_HEADERS.recommendations)
  const recommendationsBlock = extractSection(safeText, SECTION_HEADERS.recommendations, SECTION_HEADERS.pathologies)
  const pathologiesBlock = extractSection(safeText, SECTION_HEADERS.pathologies, SECTION_HEADERS.imageQuality)
  const imageQualityBlock = extractSection(safeText, SECTION_HEADERS.imageQuality, null)

  const parsedPathologies = parsePathologyBlock(pathologiesBlock)
  const parsedRecommendations = parseRecommendationsBlock(recommendationsBlock)
  const parsedImageQuality = parseImageQualityBlock(imageQualityBlock)

  return {
    hallazgos_ojo_derecho: findingsRight || '',
    hallazgos_ojo_izquierdo: findingsLeft || '',
    impresion_diagnostica: diagnosis,
    accion_inmediata: parsedRecommendations.action,
    conducta_recomendaciones: parsedRecommendations.detail,
    patologia_concomitante_detalle: parsedPathologies.detail,
    calidad_imagenes: parsedImageQuality,
    isStructured: true,
    parsedOtrasPatologias: parsedPathologies.hasPathology
  }
}

const buildImageQualitySection = (qualityByImage = {}) => {
  const qualityEntries = Object.entries(qualityByImage)
    .filter(([, quality]) => quality && quality.value)
    .sort(([, qualityA], [, qualityB]) => {
      const eyeWeightA = qualityA.eye === 'left' ? 1 : 0
      const eyeWeightB = qualityB.eye === 'left' ? 1 : 0
      if (eyeWeightA !== eyeWeightB) return eyeWeightA - eyeWeightB
      return (qualityA.position || 0) - (qualityB.position || 0)
    })
    .map(([imageId, quality]) => {
      const eyeLabel = quality.eye === 'left' ? 'izquierdo' : 'derecho'
      const qualityLabel = QUALITY_LABELS[quality.value] || quality.value
      const imagePosition = quality.position !== undefined && quality.position !== null ? quality.position : '-'
      return `- Ojo ${eyeLabel}, imagen ${imagePosition} (id: ${imageId}): ${qualityLabel}`
    })

  if (!qualityEntries.length) {
    return 'Sin registro manual.'
  }

  return qualityEntries.join('\n')
}

export const buildStructuredReportText = (formData) => {
  const pathologySelection = parseBooleanSelection(formData.otras_patologias)
  const pathologySummary = pathologySelection === true
    ? `Si: ${asText(formData.patologia_concomitante_detalle).trim() || 'Sin detalle adicional.'}`
    : pathologySelection === false
      ? 'No'
      : 'Sin definir'
  const actionSummary = normalizeAction(formData.accion_inmediata) || 'Sin definir'

  return `${SECTION_HEADERS.findingsRight}
${asText(formData.hallazgos_ojo_derecho).trim() || ''}

${SECTION_HEADERS.findingsLeft}
${asText(formData.hallazgos_ojo_izquierdo).trim() || ''}

${SECTION_HEADERS.diagnosis}
${asText(formData.impresion_diagnostica).trim()}

${SECTION_HEADERS.recommendations}
Accion inmediata recomendada: ${actionSummary}
Detalle:
${asText(formData.conducta_recomendaciones).trim()}

${SECTION_HEADERS.pathologies}
${pathologySummary}

${SECTION_HEADERS.imageQuality}
${buildImageQualitySection(formData.calidad_imagenes)}`
}

export const getInitialReportFormState = ({ informInfo, avoidDefaults = false }) => {
  const parsedText = parseStructuredReportText(informInfo && informInfo.texto_libre)
  const hasPersistedData = Boolean(informInfo)

  const referibleSelection = hasPersistedData ? parseBooleanSelection(informInfo.referible) : ''
  const gradoSelection = hasPersistedData ? parseIntegerSelection(informInfo.grado_RD) : ''
  const sospechaSelection = hasPersistedData ? parseIntegerSelection(informInfo.sospecha_MD) : ''

  const parsedPathologiesSelection = parsedText.parsedOtrasPatologias === true || parsedText.parsedOtrasPatologias === false
    ? parsedText.parsedOtrasPatologias
    : ''

  const directPathologiesSelection = hasPersistedData ? parseBooleanSelection(informInfo.otras_patologias) : ''
  const hasPathologies = directPathologiesSelection === ''
    ? parsedPathologiesSelection
    : directPathologiesSelection

  const qualityFromInform = {}
  Object.entries((hasPersistedData && informInfo.calidad_imagenes) || {}).forEach(([imageId, quality]) => {
    if (!quality) return
    const normalizedValue = normalizeQualityValue(
      typeof quality === 'object' ? (quality.calidad_manual ?? quality.value) : quality
    )
    if (!normalizedValue) return

    const eye = typeof quality === 'object' ? normalizeEye(quality.eye) : 'right'
    const position = typeof quality === 'object' ? normalizeInteger(quality.position, 0) : 0

    qualityFromInform[imageId] = {
      value: normalizedValue,
      eye,
      position
    }
  })

  const hallazgosDerecho = hasPersistedData && asText(informInfo.hallazgos_ojo_derecho).trim()
    ? asText(informInfo.hallazgos_ojo_derecho)
    : parsedText.hallazgos_ojo_derecho || (avoidDefaults ? '' : FINDINGS_TEMPLATE)

  const hallazgosIzquierdo = hasPersistedData && asText(informInfo.hallazgos_ojo_izquierdo).trim()
    ? asText(informInfo.hallazgos_ojo_izquierdo)
    : parsedText.hallazgos_ojo_izquierdo || (avoidDefaults ? '' : FINDINGS_TEMPLATE)

  const impresionDiagnostica = hasPersistedData && asText(informInfo.impresion_diagnostica).trim()
    ? asText(informInfo.impresion_diagnostica)
    : parsedText.impresion_diagnostica

  const accionInmediata = hasPersistedData && normalizeAction(informInfo.accion_inmediata)
    ? normalizeAction(informInfo.accion_inmediata)
    : parsedText.accion_inmediata

  const conductaRecomendaciones = hasPersistedData && asText(informInfo.conducta_recomendaciones).trim()
    ? asText(informInfo.conducta_recomendaciones)
    : parsedText.conducta_recomendaciones

  const patologiaDetalle = hasPersistedData && asText(informInfo.patologia_concomitante_detalle).trim()
    ? asText(informInfo.patologia_concomitante_detalle)
    : parsedText.patologia_concomitante_detalle

  return {
    referible: referibleSelection,
    grado_RD: gradoSelection,
    sospecha_MD: sospechaSelection,
    otras_patologias: hasPathologies,
    hallazgos_ojo_derecho: hallazgosDerecho,
    hallazgos_ojo_izquierdo: hallazgosIzquierdo,
    impresion_diagnostica: impresionDiagnostica,
    accion_inmediata: accionInmediata,
    conducta_recomendaciones: conductaRecomendaciones,
    patologia_concomitante_detalle: hasPathologies === true ? patologiaDetalle : '',
    calidad_imagenes: Object.keys(qualityFromInform).length ? qualityFromInform : parsedText.calidad_imagenes
  }
}

export const toInformPayload = (formData) => {
  const referibleSelection = parseBooleanSelection(formData.referible)
  const gradoSelection = parseIntegerSelection(formData.grado_RD)
  const sospechaSelection = parseIntegerSelection(formData.sospecha_MD)
  const pathologiesSelection = parseBooleanSelection(formData.otras_patologias)

  const normalizedAction = normalizeAction(formData.accion_inmediata)
  const isRecapture = isRecaptureAction(normalizedAction)

  const normalizedData = {
    referible: isRecapture ? null : (referibleSelection === '' ? null : referibleSelection),
    grado_RD: isRecapture ? null : (gradoSelection === '' ? null : gradoSelection),
    sospecha_MD: isRecapture ? null : (sospechaSelection === '' ? null : sospechaSelection),
    otras_patologias: isRecapture ? null : (pathologiesSelection === '' ? null : pathologiesSelection),
    hallazgos_ojo_derecho: isRecapture ? '' : asText(formData.hallazgos_ojo_derecho).trim(),
    hallazgos_ojo_izquierdo: isRecapture ? '' : asText(formData.hallazgos_ojo_izquierdo).trim(),
    impresion_diagnostica: isRecapture ? '' : asText(formData.impresion_diagnostica).trim(),
    accion_inmediata: normalizedAction,
    conducta_recomendaciones: asText(formData.conducta_recomendaciones).trim(),
    patologia_concomitante_detalle: isRecapture ? '' : (pathologiesSelection === true
      ? asText(formData.patologia_concomitante_detalle).trim()
      : ''),
    calidad_imagenes: {}
  }

  Object.entries(formData.calidad_imagenes || {}).forEach(([imageId, quality]) => {
    if (!quality || !quality.value) return
    const qualityCode = normalizeQualityCode(quality.value)
    if (qualityCode === null) return
    normalizedData.calidad_imagenes[imageId] = {
      calidad_manual: qualityCode,
      eye: normalizeEye(quality.eye),
      position: normalizeInteger(quality.position, 0)
    }
  })

  return {
    referible: normalizedData.referible,
    grado_RD: normalizedData.grado_RD,
    sospecha_MD: normalizedData.sospecha_MD,
    otras_patologias: normalizedData.otras_patologias,
    hallazgos_ojo_derecho: normalizedData.hallazgos_ojo_derecho,
    hallazgos_ojo_izquierdo: normalizedData.hallazgos_ojo_izquierdo,
    impresion_diagnostica: normalizedData.impresion_diagnostica,
    accion_inmediata: normalizedData.accion_inmediata,
    conducta_recomendaciones: normalizedData.conducta_recomendaciones,
    patologia_concomitante_detalle: normalizedData.patologia_concomitante_detalle,
    calidad_imagenes: normalizedData.calidad_imagenes
  }
}
